// dependencies
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { StyleSheet, css } from "aphrodite";

// components
import Comment, {
  Placeholder as CommentPlaceholder,
} from "@cloudspire/legacy-resources/src/components/Comment";
import Review from "@cloudspire/legacy-resources/src/components/Review";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import Pagination from "@cloudspire/legacy-resources/src/components/Pagination";
import {
  List,
  ListItem,
} from "@cloudspire/legacy-resources/src/components/List";

// constants
import { ROOM_COMMENT_PER_PAGE } from "../../constants/pages";
import { searchDefaultParameters } from "../../constants/search";

// api
import { commentsRoomPage } from "@cloudspire/legacy-resources/src/api/comment";

// libraries
import { emToPx } from "@cloudspire/legacy-resources/src/libraries";

// hooks
import { useResponse } from "@cloudspire/legacy-resources/src/hooks/fetcher";

const styles = StyleSheet.create({
  comment: {
    paddingTop: emToPx(1),
    paddingBottom: emToPx(1),
    paddingLeft: emToPx(0.3125),
    paddingRight: emToPx(0.3125),
  },
});

function RoomCommentList(props) {
  const { room } = props;

  const [parameterList, setParameterList] = useState({
    page: 1,
  });

  const commentsResponse = useResponse(
    commentsRoomPage({
      roomReference: room.reference,
      query: {
        ...searchDefaultParameters,
        per_page: ROOM_COMMENT_PER_PAGE,
        ...parameterList,
      },
    })
  );

  const page = parameterList?.page;

  const $comments = useRef();

  const mounted = useRef(false);

  useEffect(
    function () {
      if (mounted.current) {
        $comments.current?.scrollIntoView?.({ behavior: "smooth" });
      }
    },
    [page]
  );

  useEffect(function () {
    mounted.current = true;

    return function () {
      mounted.current = false;
    };
  });

  /**
   * Gère le clic sur le bouton de la page précédente.
   */
  function handlePressPrevious() {
    setParameterList((previousParameterList) => ({
      page: previousParameterList.page - 1,
    }));
  }

  /**
   * Gère le clic sur le bouton de la page suivante.
   */
  function handlePressNext() {
    setParameterList((previousParameterList) => ({
      page: previousParameterList.page + 1,
    }));
  }

  const isLoading =
    (!commentsResponse.data && !commentsResponse.error) ||
    commentsResponse.isValidating;

  return (
    <div ref={$comments}>
      {isLoading ? (
        <List gutter={1}>
          {Array(ROOM_COMMENT_PER_PAGE)
            .fill(null)
            .map((_, index) => {
              return (
                <ListItem key={index}>
                  <CommentPlaceholder />
                </ListItem>
              );
            })}
        </List>
      ) : commentsResponse.data?.data ? (
        <>
          <Review reviews={room.reviews} rate={room.rate} />

          <Stack marginTop={1} />

          <List>
            {commentsResponse.data?.data.map((comment, commentIndex) => {
              return (
                <ListItem key={comment.reference}>
                  <div
                    className={css(styles.comment)}
                    style={{
                      backgroundColor:
                        0 === commentIndex % 2 ? "#fff" : "#f7f7f7",
                    }}
                  >
                    <Comment
                      publishedAt={new Date(comment.publishedAt)}
                      averageRate={comment.averageRate}
                      cosinessRate={comment.cosinessRate}
                      fairnessRate={comment.fairnessRate}
                      greetingRate={comment.greetingRate}
                      qualityRate={comment.qualityRate}
                      title={comment.title}
                      customerFullname={comment.customerName}
                      composition={comment.composition}
                      customerContent={comment.customerContent}
                      ownerContent={comment.ownerContent}
                      agentContent={comment.agentContent}
                      ownerFullname={comment.ownerName}
                    />
                  </div>
                </ListItem>
              );
            })}
          </List>

          <Stack marginTop={1} />

          <Pagination
            currentPage={parameterList.page}
            itemCount={commentsResponse.data?.meta?.total ?? 0}
            itemPerPage={ROOM_COMMENT_PER_PAGE}
            nextButtonProps={{
              onPress: handlePressNext,
            }}
            previousButtonProps={{
              onPress: handlePressPrevious,
            }}
            shouldRenderBackwardButton={false}
            shouldRenderForwardButton={false}
            shouldRenderItemCount={false}
            shouldRenderPageCount={false}
          />
        </>
      ) : null}
    </div>
  );
}

RoomCommentList.propTypes = {
  room: PropTypes.object,
};

export default RoomCommentList;
